/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import React, { useEffect, useState, useCallback } from 'react';
import type { WrappedComponentProps } from 'react-intl-next';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import AkSpinner from '@atlaskit/spinner/spinner';
import AkTableTree, {
	Headers,
	Header,
	Row,
	Rows,
	Cell,
	TableTreeDataHelper,
} from '@atlaskit/table-tree';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { ErrorSectionMessage } from '@confluence/error-boundary/entry-points/ErrorSectionMessage';
import { ObservableSpinner } from '@confluence/infinite-scroll';
import { CONTEXT_PATH } from '@confluence/named-routes';
import { NoChildrenBullet } from '@confluence/page-selection-table/entry-points/NoChildrenBullet';
import { canArchiveContent } from '@confluence/content-types-utils/entry-points/contentOperationsUtils';

import { fireScreenEvent, fireUIClickEvent, getActionName } from './AnalyticsEvents';
import type {
	PageTreeWithArchivePermissionsQuery_content_PaginatedContentListWithChild_nodes_Content,
	PageTreeWithArchivePermissionsQuery_content_PaginatedContentListWithChild_nodes_Content_children_PaginatedContentList_nodes_Content,
	PageTreeWithArchivePermissionsQuery as PageTreeWithArchivePermissionsQuery$data,
	PageTreeWithArchivePermissionsQueryVariables as PageTreeWithArchivePermissionsQuery$variables,
} from './__types__/PageTreeWithArchivePermissionsQuery';
import { CONTENT_STATUS_ARCHIVED } from './archivingConstants';

// 1. Expand the table until a certain height, then start internal scrolling.
//    We use view height to make a rough guesstimate for max height because the Atlaskit modal component,
//    which will be the parent of this component, dynamically resizes modal height to fit content until
//    it reaches a certain number of px away from the edge of the viewport. We want to take advantage of
//    this by letting the entire table + modal expand in height until it reaches a certain point,
//    after which the table should start internal scrolling.
// 2. For the row hover state,
//    we must wrap all table rows in a single parent container to apply row-specific
//    style overrides, because of limitations with rendering in AK table tree.
//    The best we can do to isolate each row element where styles should apply,
//    since Rows/Row do not accept classname prop, is the [role] attribute.
//    Also, only way to target the spinner rows, which somehow don't get put into a
//    div[role="row"], is to target the first child role["treegrid"]
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableTreeContentsContainer = styled.div({
	overflowY: 'scroll',
	overflowX: 'hidden',
	maxHeight: '30vh',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div[role="row"]:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
		borderRadius: token('radius.xsmall'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div[role="row"]': {
		borderBottom: 'none',
		maxHeight: token('space.500'),
		paddingLeft: token('space.075'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div[role="treegrid"] > div': {
		borderBottom: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'a.rowTitle': {
		color: token('color.text'),
		textDecoration: 'underline',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'a.rowTitle:focus': {
		border: token('border.width.focused'),
		borderStyle: 'solid',
		borderColor: token('color.border.focused'),
		borderRadius: token('radius.small'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledTableHeaders = styled.span({
	color: token('color.text.subtlest'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div[role="columnheader"]': {
		paddingRight: token('space.0'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'div[role="columnheader"]:first-of-type': {
		paddingLeft: token('space.400') /* aligning title column with page title */,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'div#permission-header': {
		paddingRight: token('space.300'),
		justifyContent: 'flex-end',
	},
});

// right-align the permissions icons in table
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-unsafe-style-overrides -- To migrate as part of go/ui-styling-standard
const PermissionCell = styled(Cell)({
	display: 'flex',
	flexDirection: 'row-reverse',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpinnerContainer = styled.div({
	width: 0,
	margin: `${token('space.0')} auto`,
	padding: `${token('space.100')} ${token('space.0')}`,
});

export const i18n = defineMessages({
	archivePermissionColumnHeader: {
		id: 'archive-pages.nested-page-tree.archive-permissions-column',
		defaultMessage: 'Permission to archive',
		description:
			'Header for the column showing whether the current user has access to archive each page in the nested page tree',
	},
	canArchiveLabel: {
		id: 'archive-pages.nested-page-tree.can-archive',
		defaultMessage: 'You have permission to archive',
		description:
			'Label text on a green check icon explaining that user has permission to archive this page',
	},
	noPermissionArchiveLabel: {
		id: 'archive-pages.nested-page-tree.no-permission',
		defaultMessage: 'You do not have permission to archive',
		description:
			'Label text on a red cross circle icon explaining that user does not have permission to archive this page',
	},

	nestedContentColumnHeader: {
		id: 'archive-pages.nested-content-tree.nested-content-column',
		defaultMessage: 'Content',
		description:
			'Header for the column showing nested page titles, in a table showing permissions of a nested page tree',
	},
	limitedContentArchivePermissionTooltip: {
		id: 'archive-pages.nested-content-tree.archive.limited-permissions',
		defaultMessage: 'You don’t have permission to archive one or more nested items.',
		description:
			'Tooltip text over a warning icon in the nested content tree hierarchy of the archive nested content confirmation dialog, explaining that the content shown has nested content for which the user does not have archive permission',
	},
	limitedContentRestorePermissionTooltip: {
		id: 'archive-pages.nested-content-tree.restore.limited-permissions',
		defaultMessage: 'You don’t have permission to restore one or more nested items.',
		description:
			'Tooltip text over a warning icon in the nested content tree hierarchy of the restore nested content confirmation dialog, explaining that the content shown has nested content for which the user does not have restore permission',
	},
	errorContentTreeTitle: {
		id: 'archive-pages.nested-content-tree.error.title',
		defaultMessage: 'We couldn’t load the whole content tree',
		description:
			'Title for a warning message letting the user know that we failed to load the whole content tree',
	},
});

export const DEFAULT_PAGE_SIZE = 25;

type PageTreeProps = {
	contentId: string;
	analyticsSource: string;
	isArchive: boolean;
	pageSize?: number;
};
type PageTreeItemType = {
	id: string;
	title: string;
	canArchiveOrUnarchive: boolean;
	hasLimitedDescendantPermissions: boolean;
	hasChildren: boolean;
	spaceKey: string;
	parentId: string;
	parentItem: PageTreeItemType | null;
	href: string;
};
type ParentPageLoadingType = {
	offset?: number | null;
	hasNextPage?: boolean | null;
	lastLoadedChildId: string | null;
	isLoading?: boolean;
};

const tableTreeDataHelper = new TableTreeDataHelper<PageTreeItemType>({
	key: 'id',
});

function PageTreeWithArchivePermissionsComponent({
	contentId,
	analyticsSource,
	isArchive,
	pageSize = DEFAULT_PAGE_SIZE,
	intl,
}: PageTreeProps & WrappedComponentProps) {
	// parentPageLoadingMap is a mutable object and functions more like a ref; setting
	// values won't trigger a rerender which is what we want (only using it to track
	// pagination values for queries). Maps parent page ID to its loading state.
	const [parentPageLoadingMap, setParentPageLoadingMap] = useState<
		Map<string, ParentPageLoadingType>
	>(new Map());
	// Keep track of which rows are expanded through a simple array, which will trigger
	// a rerender on update; we may display some content in each row depending on whether it is
	// expanded. Doesn't belong in page tree data, which is permanent / not susceptible to changes.
	const [expandedRows, setExpandedRows] = useState<string[]>([]);
	const [pageTree, setPageTree] = useState<PageTreeItemType[]>([]);
	const [queryError, setQueryError] = useState<Error | undefined>(undefined);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data, fetchMore, loading, error } = useQuery<
		PageTreeWithArchivePermissionsQuery$data,
		PageTreeWithArchivePermissionsQuery$variables
	>(
		gql`
			query PageTreeWithArchivePermissionsQuery($contentId: ID!, $first: Int!, $offset: Int!) {
				content(id: $contentId) {
					nodes {
						children(offset: $offset, first: $first) {
							count
							nodes {
								id
								title
								status
								operations {
									operation
									targetType
								}
								links {
									webui
								}
								visibleDescendantsCount
								archivableDescendantsCount
							}
							pageInfo {
								hasNextPage
							}
						}
						space {
							key
							id
						}
						id
					}
				}
			}
		`,
		{
			notifyOnNetworkStatusChange: true,
			fetchPolicy: 'cache-and-network',
			variables: { contentId, first: pageSize, offset: 0 },
		},
	);

	useEffect(() => {
		fireScreenEvent({
			createAnalyticsEvent,
			name: `${getActionName(isArchive)}PageTreeWithArchivePermissions`,
		});
	}, [createAnalyticsEvent, isArchive]);

	const getContentNodeFromData = (
		data: PageTreeWithArchivePermissionsQuery$data | undefined,
		contentId: string,
	) => {
		const index = (data?.content?.nodes || []).findIndex((node) => node?.id === contentId);
		return {
			index,
			node: index !== -1 ? data!.content!.nodes![index] : null,
		};
	};

	const extractChildPagesData = useCallback(
		(
			contentNode: PageTreeWithArchivePermissionsQuery_content_PaginatedContentListWithChild_nodes_Content,
			parentItem = null,
		) => {
			const children = contentNode?.children?.nodes || [];

			return children.reduce(
				(
					pages: PageTreeItemType[],
					currentPage: PageTreeWithArchivePermissionsQuery_content_PaginatedContentListWithChild_nodes_Content_children_PaginatedContentList_nodes_Content | null,
				) => {
					if (!currentPage) {
						return pages;
					}

					// for archive confirmation dialog, filter out archived pages,
					// for restore confirmation dialog, all pages should be archived so no need to filter out any child pages.
					// if we do show child pages of current status then we have a major flaw in our archiving logic
					if ((isArchive && currentPage.status !== CONTENT_STATUS_ARCHIVED) || !isArchive) {
						const {
							id,
							title,
							operations,
							archivableDescendantsCount,
							visibleDescendantsCount,
							links,
						} = currentPage;
						pages.push({
							id: id || '',
							title: title || '',
							canArchiveOrUnarchive: canArchiveContent(operations || []),
							hasLimitedDescendantPermissions: archivableDescendantsCount < visibleDescendantsCount,
							hasChildren: visibleDescendantsCount > 0,
							parentId: contentNode?.id || '',
							spaceKey: contentNode?.space?.key || '',
							parentItem,
							href: links?.webui ? `${CONTEXT_PATH}${links.webui}` : '',
						});
					}
					return pages;
				},
				[],
			);
		},
		[isArchive],
	);

	const fetchPaginatedData = useCallback(
		(contentId: string, parentItem = null) => {
			const parentItemInMap = parentPageLoadingMap.get(contentId);
			// If parent isn't in map (aka never requested its children before),
			// or if parent IS in map but we haven't loaded all its data, fetch more
			if (!parentItemInMap || (parentItemInMap.hasNextPage && !parentItemInMap.isLoading)) {
				if (parentItemInMap) {
					setParentPageLoadingMap(
						parentPageLoadingMap.set(contentId, {
							...parentItemInMap,
							isLoading: true,
						}),
					);
				}
				const offset = parentItemInMap ? parentItemInMap.offset : 0;
				fetchMore({
					variables: { contentId, first: pageSize, offset },
					// Use "any" for results type just so that we don't have to silence
					// or disable TS on every line of this function
					updateQuery: (prev: any, { fetchMoreResult }) => {
						if (!fetchMoreResult) return prev;

						const fetchMoreNode = fetchMoreResult.content.nodes[0];
						// The main query for this component can get executed many times with disparate inputs
						// (different content IDs and different pagination values). Even though each individual query
						// execution will return a single content node (with possibly many children), we may have
						// multiple content nodes to track when merging, from various executions with different content
						// IDs, so do additional logic to maintain the entire resultset.
						const { index, node: prevNode } = getContentNodeFromData(prev, contentId);
						const prevNodeChildren = prevNode?.children?.nodes ?? [];

						const newCount = prevNodeChildren.length + fetchMoreNode.children.nodes.length;
						const newParentNode = {
							...fetchMoreNode,
							children: {
								...fetchMoreNode.children,
								count: newCount,
								nodes: [
									// add on this content node's previous existing children if necessary
									...prevNodeChildren,
									...fetchMoreNode.children.nodes,
								],
								pageInfo: {
									...fetchMoreNode.children.pageInfo,
								},
								__typename: 'PaginatedContentList',
							},
						};
						let newNodes;
						if (index === -1) {
							newNodes = [...prev.content.nodes, newParentNode];
						} else {
							prev.content.nodes.splice(index, 1, newParentNode);
							newNodes = [...prev.content.nodes];
						}
						const newData = {
							content: {
								nodes: newNodes,
								__typename: 'PaginatedContentListWithChild',
							},
						};

						return newData;
					},
				})
					.then((result) => {
						const { data } = result;
						// we only receive the NEW data in this callback, so it will always be a single content node at index 0
						const newContentNode = data?.content?.nodes?.[0];
						const parentId = newContentNode?.id;
						const pageInfo = newContentNode?.children?.pageInfo;
						const prevOffset = parentItemInMap ? parentItemInMap.offset : 0;
						const childCount = parseInt(newContentNode?.children?.count + prevOffset);
						const childItems = extractChildPagesData(newContentNode, parentItem);

						// Keep track of where we left off with loading this parent's children
						setParentPageLoadingMap(
							parentPageLoadingMap.set(parentId, {
								...parentItemInMap,
								hasNextPage: pageInfo && pageInfo.hasNextPage,
								offset: childCount,
								lastLoadedChildId: childItems.length ? childItems[childItems.length - 1].id : null,
								isLoading: false,
							}),
						);

						if (prevOffset === 0) {
							// add child items for the first time
							setPageTree(tableTreeDataHelper.updateItems(childItems, pageTree, parentItem));
						} else {
							// append child items onto an existing list
							setPageTree(tableTreeDataHelper.appendItems(childItems, pageTree, parentItem));
						}
					})
					.catch((error) => {
						// fetchMore errors are not surfaced in initial useQuery error.
						setQueryError(error);
					});
			}
		},
		[extractChildPagesData, fetchMore, pageSize, pageTree, parentPageLoadingMap],
	);

	useEffect(() => {
		// Construct page tree for first time on mount.
		// We may have multiple query results in the cache, and need to find top level children (matching prop contentId)
		const { node } = getContentNodeFromData(data, contentId);
		if (node && !pageTree.length) {
			const hasNextPage = node?.children?.pageInfo?.hasNextPage;
			const childCount = node?.children?.count;

			const childItems = extractChildPagesData(node);
			// If this component is being re-mounted (eg after clicking 'hide' and then 'show' from the confirmation dialog),
			// we already have page tree data loaded locally, so don't reset our loading map.
			// Resetting it could cause us to re-fetch and duplicate subsequent pages of data that we already have loaded.
			if (!parentPageLoadingMap.has(contentId)) {
				setParentPageLoadingMap(
					parentPageLoadingMap.set(contentId, {
						hasNextPage,
						offset: childCount,
						lastLoadedChildId: childItems.length ? childItems[childItems.length - 1].id : null,
					}),
				);
			}

			if (!childItems.length) {
				// it's possible for us to have data but no child page items, e.g. if
				// every child in the first X pages of data happens to be status = archived
				// In that case, we need to start fetching the next page of data manually.
				// Usually, we'll start fetching more data "on demand" from the user --
				// aka when they scroll into view a dynamically generated spinner row (see LoadRowsSpinner)
				// that automatically triggers fetching the next page of data for any parent.
				// But in this case, we've already fetched X pages of data and STILL have nothing to
				// render except our initial static "give me a sec, I'm loading" spinner. So keep fetching!
				fetchPaginatedData(contentId);
			} else {
				// begin rendering our page tree if we have data
				setPageTree(tableTreeDataHelper.updateItems(childItems, pageTree));
			}
		}
	}, [
		data,
		setPageTree,
		pageTree,
		contentId,
		parentPageLoadingMap,
		extractChildPagesData,
		fetchPaginatedData,
	]);

	const loadChildren = (parentItem) => {
		if (parentItem) {
			// we are loading more children for a specific parent row in the table
			fetchPaginatedData(parentItem.id, parentItem);
		} else {
			// we are loading top-level children (not linked to a parent row)
			fetchPaginatedData(contentId);
		}
	};

	// While there are currently only two interactions that we track (expand & collapse), they are
	// defined as a set of values (instead of a binary) to more easily support additional tracked
	// interactions in the future.
	type PageTreeUIInteractionType = 'expand' | 'collapse';

	const firePageTreeUIEvent = useCallback(
		(interaction: PageTreeUIInteractionType, contentId: string) => {
			fireUIClickEvent({
				createAnalyticsEvent,
				actionSubject: 'button',
				actionSubjectId: `${interaction}ParentPage`,
				source: analyticsSource,
				attributes: { contentId },
			});
		},
		[createAnalyticsEvent, analyticsSource],
	);

	const expandParent = (parentItem) => {
		if (!expandedRows.includes(parentItem.id)) {
			firePageTreeUIEvent('expand', parentItem.id);
			setExpandedRows([...expandedRows, parentItem.id]);
		}

		// If parentItem already has children locally (e.g. case where user is reexpanding after a previous collapse),
		//   don't do anything; the table tree component will take care of rendering the children.
		// If parentItem has no children in the page tree BUT has cached data (e.g. from a previous mount), construct
		//   the nested page tree without making a network call. (If not all child data was loaded the first time around,
		//   we'll have tracked that in the loading state map and will fetch the next page of child data when user scrolls.
		// Else, make a network call.
		if (!parentItem.children || !parentItem.children.length) {
			const { node } = getContentNodeFromData(data, parentItem.id);
			if (node) {
				setPageTree(
					tableTreeDataHelper.updateItems(
						extractChildPagesData(node, parentItem),
						pageTree,
						parentItem,
					),
				);
			} else {
				// we have no cached data to construct children from so
				// go ahead and load it ad-hoc
				loadChildren(parentItem);
			}
		}
	};

	const collapseParent = (parentItem) => {
		if (expandedRows.includes(parentItem.id)) {
			firePageTreeUIEvent('collapse', parentItem.id);
			setExpandedRows(expandedRows.filter((id) => id !== parentItem.id));
		}
	};

	// TS complains if I put this actual expression inside the jsx. :shrug:
	const anyError = queryError || error;
	return (
		<AkTableTree>
			<StyledTableHeaders>
				<Headers>
					<Header width={375}>
						<FormattedMessage {...i18n.nestedContentColumnHeader} />
					</Header>
					<Header width={230} id="permission-header">
						<FormattedMessage {...i18n.archivePermissionColumnHeader} />
					</Header>
				</Headers>
			</StyledTableHeaders>
			{anyError ? (
				<ErrorSectionMessage title={i18n.errorContentTreeTitle} error={anyError} />
			) : null}
			{pageTree.length ? (
				<TableTreeContentsContainer id="table-tree-body">
					<Rows
						items={pageTree}
						render={({
							id,
							title,
							children,
							hasChildren,
							canArchiveOrUnarchive,
							hasLimitedDescendantPermissions,
							parentId,
							parentItem,
							spaceKey,
							href,
						}) =>
							// TableTree is very picky about exactly what is returned in the <Rows> render() function.
							// To get children rows to expand and display properly, the row must have <Row> as the outermost
							// React node (for example, wrapping it even with <Fragment> messes up the rendering), which is why
							// we call a function to get the row contents rather than using a function component.
							// Unfortunately this render pattern seems to mean that EVERY row rerenders whenever data updates, even
							// unrelated (not-same-level) rows. :sad-face:
							getRow({
								id,
								title,
								childItems: children || [],
								hasChildren,
								canArchiveOrUnarchive,
								hasLimitedDescendantPermissions,
								parentId,
								parentItem,
								loadMoreData: loadChildren,
								expandRow: expandParent,
								collapseRow: collapseParent,
								spaceKey,
								parentPageLoadingMap,
								expandedRows,
								isArchive,
								intl,
								href,
							})
						}
					/>
				</TableTreeContentsContainer>
			) : (
				!anyError &&
				loading && (
					<SpinnerContainer>
						<AkSpinner size="medium" testId="archive-pagetree-loading-spinner" />
					</SpinnerContainer>
				)
			)}
		</AkTableTree>
	);
}

export const PageTreeWithArchivePermissions = injectIntl(PageTreeWithArchivePermissionsComponent);

type getRowArgs = PageTreeItemType & {
	expandRow: (parentItem: PageTreeItemType) => void;
	collapseRow: (parentItem: PageTreeItemType) => void;
	parentId: string;
	// parentItem will be empty for top level rows
	parentItem: PageTreeItemType | null;
	childItems: PageTreeItemType[];
	loadMoreData: (parentItem: PageTreeItemType | null) => void;
	parentPageLoadingMap: Map<string, ParentPageLoadingType>;
	expandedRows: string[];
	isArchive: boolean;
	href: string;
};
// getRow exists so that we have a way to dynamically render a spinner
// beneath any currently paginating list of children. We take the last child
// row for the given parent and render a spinner *instead* of it. The reason
// we have to do this hack is there's no built-in way to render anything other
// than a <Row> between other rows in the TableTree component. We also can't
// dynamically throw an extra <Row> in the <Rows> render function as it has
// no awareness of index and can only render a single row at a time. So the
// only way to do this is either to 1) substitute a row for a spinner, or 2)
// add an extra page tree "child" item for incomplete datasets. #2 is messy
// as it involves messing with actual page tree data so went with #1.
export const getRow = ({
	id,
	childItems,
	hasChildren,
	expandRow,
	collapseRow,
	title,
	canArchiveOrUnarchive,
	hasLimitedDescendantPermissions,
	parentId,
	parentItem,
	loadMoreData,
	parentPageLoadingMap,
	expandedRows,
	isArchive,
	intl,
	href,
}: getRowArgs & WrappedComponentProps) => {
	// If this is the last loaded child for its parent, but its parent
	// hasn't finished loading all pages of data, display a spinner
	const parentLoadingItem = parentPageLoadingMap.get(parentId);

	return parentLoadingItem &&
		parentLoadingItem.hasNextPage &&
		parentLoadingItem.lastLoadedChildId === id ? (
		<Row>
			<Cell>
				<ObservableSpinner
					onScrollTo={() => loadMoreData(parentItem)}
					testId="archive-pagetree-paginating-spinner"
					observerOptions={{
						root: document.getElementById('table-tree-body'),
						rootMargin: '100px 0px 0px 0px',
					}}
				/>
			</Cell>
		</Row>
	) : (
		<Row
			itemId={id}
			items={childItems}
			hasChildren={hasChildren}
			onExpand={expandRow}
			onCollapse={collapseRow}
		>
			<Cell singleLine>
				{
					// Table tree prepends parent rows with clickable chevron icon; we add on
					// a static aligned bullet icon for non-parent rows
					!hasChildren ? <NoChildrenBullet /> : null
				}
				{/* eslint-disable-next-line @atlaskit/design-system/no-html-anchor, @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
				<a className="rowTitle" target="_blank" href={href}>
					{title}
				</a>
			</Cell>
			<PermissionCell singleLine>
				{
					// only show the 'limited permissions' icon, indicating that current row has descendants
					// with no archive permission, if descendants are not expanded
					!expandedRows.includes(id) && hasLimitedDescendantPermissions ? (
						<Tooltip
							content={
								isArchive
									? intl.formatMessage(i18n.limitedContentArchivePermissionTooltip)
									: intl.formatMessage(i18n.limitedContentRestorePermissionTooltip)
							}
							tag="span"
							position="top"
						>
							<StatusWarningIcon
								spacing="spacious"
								label=""
								color={token('color.icon.warning')}
								testId={`limited-permissions-icon-${id}`}
							/>
						</Tooltip>
					) : null
				}
				{canArchiveOrUnarchive ? (
					<StatusSuccessIcon
						spacing="spacious"
						label={intl.formatMessage(i18n.canArchiveLabel)}
						color={token('color.icon.success')}
						testId={`has-archive-permission-icon-${id}`}
					/>
				) : (
					<CrossCircleIcon
						spacing="spacious"
						label={intl.formatMessage(i18n.noPermissionArchiveLabel)}
						color={token('color.icon.danger')}
						testId={`no-archive-permission-icon-${id}`}
					/>
				)}
			</PermissionCell>
		</Row>
	);
};
